import { dateValueOf, typeDate } from "./date.js";
import type { PeriodInput, SearchPeriod } from "./search.js";
import { resolvePeriod } from "./search.js";

/**
 * 業務の言葉の対応表と、AI回答の頭の「解釈の1行」（#224 の手直し、docs/90 §13.3。2026-10-21 射場さんの本番の確かめ）。
 *   - 解釈が割れる言葉（請求済み・今月・未入金・返事待ち…）の意味を1か所に置き、1回目のツール選びの指示（apps/api/src/search/llm.ts）にそのまま渡す
 *   - 意味はホームの数字と同じ決まりにそろえる（未入金＝ホームの「未入金」、未受注＝ホームの「返事を待っている」、見積の月＝発行日）
 *   - 答えの頭の解釈の1行は API がツールの引数から組み立てる（LLM に書かせない）。言葉はここの DOC_STATE_TEXT など＝画面の言葉（未受注・発行済）
 *
 * このファイルだけは利用者が質問に書く言葉（「返事待ち」「請求済み」など）を文字で持つ＝AI がその言葉を読み取るため。
 * 画面に出す文（解釈・質問例）はこのファイルでも画面の言葉で書く（apps/api/test/screen-text.test.ts の t はこのファイルの TERM_WORDS だけを外す）
 */

/** 帳票の種類（状態で探す・集計の対象） */
export const DOC_QUERY_KINDS = ["estimate", "invoice", "receipt"] as const;
export type DocQueryKind = (typeof DOC_QUERY_KINDS)[number];
export const DOC_QUERY_KIND_TEXT: Record<DocQueryKind, string> = { estimate: "見積書", invoice: "請求書", receipt: "領収書" };

/**
 * 帳票の状態（業務の言葉。DB の documents.status・projects.order_status・入金の残から API が決める）。
 * 何も付けないときは issued（下書き・取消を除く全部）
 */
export const DOC_STATES = ["issued", "draft", "waiting", "ordered", "lost", "expired", "unpaid", "overdue", "paid", "voided"] as const;
export type DocState = (typeof DOC_STATES)[number];
/** 画面の言葉（解釈の1行に出す） */
export const DOC_STATE_TEXT: Record<DocState, string> = {
  issued: "発行済",
  draft: "下書き",
  waiting: "未受注",
  ordered: "受注",
  lost: "失注",
  expired: "期限切れ",
  unpaid: "未入金",
  overdue: "期限超過",
  paid: "入金済",
  voided: "取消",
};
/** 状態の意味（解釈の1行の（ ）と AI への説明） */
export const DOC_STATE_MEANING: Record<DocState, string> = {
  issued: "下書き・取消を除く。受注・失注も含む",
  draft: "まだ発行していない",
  waiting: "発行済で受注・失注になっていない",
  ordered: "受注にした",
  lost: "失注にした",
  expired: "未受注で有効期限が今日より前",
  unpaid: "残高がある。請求日を問わない",
  overdue: "残高があり、お支払期限が今日より前",
  paid: "残高が0",
  voided: "取り消した",
};
/** 状態ごとに使える種類 */
export const DOC_STATE_KINDS: Record<DocState, readonly DocQueryKind[]> = {
  issued: DOC_QUERY_KINDS,
  draft: DOC_QUERY_KINDS,
  voided: DOC_QUERY_KINDS,
  waiting: ["estimate"],
  ordered: ["estimate"],
  lost: ["estimate"],
  expired: ["estimate"],
  unpaid: ["invoice"],
  overdue: ["invoice"],
  paid: ["invoice"],
};
/** 残高を足す状態（金額は請求額でなく残高） */
export const BALANCE_STATES: readonly DocState[] = ["unpaid", "overdue"];
/** 受注日（失注日）で月を切る状態 */
export const DECIDED_STATES: readonly DocState[] = ["ordered", "lost"];

/** 月を切る日（解釈の1行） */
export function docDateBasis(kind: DocQueryKind, state: DocState | undefined): string {
  if (kind === "estimate") return state === "ordered" ? "受注日" : state === "lost" ? "失注日" : state === "draft" ? "見積日" : "発行日";
  return kind === "invoice" ? "請求日" : "領収日";
}

/**
 * 業務の言葉の対応表。words＝利用者が書く言葉（AI が読み取る）、meaning＝意味（ホームと同じ決まり）、call＝どのツールをどの条件で呼ぶか。
 * 1回目のツール選びの指示に searchTermsPrompt() でそのまま渡す。足すときはここに1行（docs/90 §13.3 の表も）
 */
export const SEARCH_TERMS: readonly { words: readonly string[]; meaning: string; call: string }[] = [
  { words: ["今月", "先月", "今年", "昨年", "去年"], meaning: "日本時間の暦の月・年（今月＝今月1日〜末日）", call: "period に this_month・last_month・this_year・last_year。月を名指しされたら month（YYYY-MM）" },
  { words: ["見積の合計", "見積の金額", "出した見積"], meaning: "発行済の見積書（下書き・取消を除く。受注・失注も含む）の税込の合計。月は発行日（ホームの「今月出した見積」と同じ）", call: "amounts_sum kind=estimate" },
  { words: ["請求済み", "請求した", "請求の合計", "請求額", "売上"], meaning: "発行済の請求書（下書き・取消を除く）の税込の合計。月は請求日", call: "amounts_sum kind=invoice" },
  { words: ["未入金", "入金待ち", "売掛", "回収"], meaning: "発行済の請求書の残高（請求額−入金・手数料・値引き）。請求日を問わない今の残高（ホームの「未入金」と同じ）。「今月の未入金」も期間を付けない", call: "documents_by_status kind=invoice state=unpaid（period は付けない）" },
  { words: ["入金遅れ", "支払期限切れ", "支払いが遅れている"], meaning: "未入金のうちお支払期限が今日より前", call: "documents_by_status kind=invoice state=overdue" },
  { words: ["入金済み", "入金があった"], meaning: "残高が0の請求書", call: "documents_by_status kind=invoice state=paid" },
  { words: ["返事待ち", "未受注", "回答待ち", "出したまま"], meaning: "発行済の見積書で受注・失注になっていないもの（ホームの「返事を待っている」と同じ）", call: "documents_by_status kind=estimate state=waiting" },
  { words: ["期限切れの見積", "有効期限切れ", "有効期限が過ぎた"], meaning: "未受注の見積書で有効期限が今日より前（有効期限の欄の「御見積後2週間」「3ヶ月」「2026年10月31日」を見積日から数える）。有効期限が空・読めない見積書は判定できず対象外に数える", call: "documents_by_status kind=estimate state=expired" },
  { words: ["受注", "受注した", "決まった"], meaning: "受注にした見積書。月は受注日", call: "一覧は documents_by_status kind=estimate state=ordered、合計は amounts_sum kind=estimate state=ordered" },
  { words: ["失注"], meaning: "失注にした見積書。月は失注日", call: "documents_by_status kind=estimate state=lost" },
  { words: ["下書き"], meaning: "まだ発行していない帳票", call: "documents_by_status state=draft" },
  { words: ["案件の金額", "大きい案件", "案件の売上"], meaning: "受注した{project}の税抜の受注額（受注日で数える）", call: "amounts_sum kind=project_group sort=amount_desc" },
  { words: ["粗利", "利益", "利益率"], meaning: "受注した{project}の 税抜−仕入れ。仕入れ未入力の{project}は対象外", call: "amounts_sum kind=project_group metric=margin（n％未満は marginBelowPct）" },
  { words: ["万円以上", "円以上", "円以下", "より高い", "より安い"], meaning: "帳票の税込の金額で比べる", call: "documents_by_status の minAmount・maxAmount に質問の書き方のまま（例「100万円」）" },
  { words: ["一番大きい", "一番高い", "多い順", "高い順"], meaning: "税込の金額の大きい順", call: "sort=amount_desc（{project}は amounts_sum kind=project_group sort=amount_desc）" },
  { words: ["宛て", "向け", "さんの", "様の"], meaning: "取引先の名前で絞る", call: "partner に取引先の名前（「宛て」「様」「株式会社」は付けない）" },
  { words: ["比べて", "前月比", "前年比", "増えた", "減った"], meaning: "2つの期間の合計の差", call: "amounts_compare（periodA が新しい方・periodB がくらべる方）" },
  { words: ["単価", "いくらで出して", "相場"], meaning: "明細の名称（品名）の過去の単価", call: "lines_unit_price_history name=品名" },
];

/**
 * 質問の言葉から期間（今月・先月・今年・昨年）を決める。AI がツールに期間を付け忘れたときだけ API が補う（本番の確かめで「今月請求済み」の period が抜けた＝docs/90 §12.8）。
 * 2つ以上の期間の言葉があれば（「今月と先月」）補わない＝比較の道具に任せる。「年度」は会計年度なので暦の年にしない
 */
export function periodFromQuestion(q: string): SearchPeriod | undefined {
  const t = q.normalize("NFKC");
  const hits: SearchPeriod[] = [];
  if (/今月|当月|本月/.test(t)) hits.push("this_month");
  if (/先月|前月/.test(t)) hits.push("last_month");
  if (/(今年|本年)(?!度)/.test(t)) hits.push("this_year");
  if (/(昨年|去年|前年)(?!度|比)/.test(t)) hits.push("last_year");
  return hits.length === 1 ? hits[0] : undefined;
}
/** 期間を何も付けていないか（period・month・from・to のどれも無い） */
export const noPeriod = (p: PeriodInput): boolean => !p.period && !p.month && !p.from && !p.to;

/** 1回目のツール選びの指示に入れる対応表（言葉 → 意味 → 呼び方） */
export function searchTermsPrompt(): string {
  return SEARCH_TERMS.map((t) => `- 「${t.words.join("」「")}」：${t.meaning} → ${t.call}`).join("\n");
}

/** 画面の質問例（検索の欄が空のとき。AI回答のある会社だけ。本番で正しく答えた質問から＝docs/90 §12.8）。{project} は画面で t() */
export const SEARCH_EXAMPLES = [
  "今月の未入金はいくら？",
  "未受注の見積は？",
  "先月の見積の合計は？",
  "100万円以上の見積は？",
  "今月の粗利 15％未満の{project}は？",
] as const;

// ---------------------------------------------------------------------------
// 有効期限（見積書の「有効期限」の欄は文＝「御見積後2週間」「3ヶ月」。期限切れの判定に日付へ直す）
// ---------------------------------------------------------------------------

const pad2 = (n: number) => String(n).padStart(2, "0");
/** YYYY-MM-DD に日・月・年を足す（月末を超えたら月末＝1/31 の1ヶ月後は 2/28） */
function addToDate(ymd: string, n: number, unit: "day" | "month" | "year"): string {
  const [y, m, d] = ymd.split("-").map(Number) as [number, number, number];
  if (unit === "day") {
    const t = new Date(Date.UTC(y, m - 1, d + n));
    return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`;
  }
  const months = unit === "year" ? n * 12 : n;
  const total = y * 12 + (m - 1) + months;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  const last = new Date(Date.UTC(ny, nm, 0)).getUTCDate();
  return `${ny}-${pad2(nm)}-${pad2(Math.min(d, last))}`;
}

/**
 * 有効期限の日（YYYY-MM-DD）。決まりは1か所（期限切れの判定＝docs/90 §13.3）：
 *   1) 日付の列（valid_until）があればそれ
 *   2) 文が日付（「2026年10月31日」「R8.10.31」「2026/10/31まで」）ならその日
 *   3) 文に「n日」「n週間」「nヶ月（か月・カ月）」「n年」があれば 見積日＋その長さ（「御見積後2週間」＝見積日の 14日後）
 *   4) どれでもない（空・「別途協議」）なら null＝判定できない
 */
export function validityDeadline(text: string | null | undefined, issueDate: string | null | undefined, validUntil?: string | null): string | null {
  if (validUntil) return validUntil.slice(0, 10);
  const t = (text ?? "").normalize("NFKC").replace(/\s+/g, "");
  if (!t) return null;
  const dateText = /((?:明治|大正|昭和|平成|令和|[MTSHR])?(?:元|\d{1,4})[年.\/-]\d{1,2}[月.\/-]\d{1,2}日?)/i.exec(t)?.[1];
  if (dateText) {
    const v = typeDate(dateText);
    if (v) return dateValueOf(v);
  }
  if (!issueDate) return null;
  const m = /(\d{1,3})(日間|日|週間|週|ヶ月|か月|カ月|ヵ月|ケ月|箇月|ヶ年|年間|年)/.exec(t);
  if (!m) return null;
  const n = Number(m[1]);
  if (!(n > 0)) return null;
  const u = m[2]!;
  const base = issueDate.slice(0, 10);
  if (u.startsWith("日")) return addToDate(base, n, "day");
  if (u.startsWith("週")) return addToDate(base, n * 7, "day");
  if (u.endsWith("年") || u === "年間") return addToDate(base, n, "year");
  return addToDate(base, n, "month");
}

// ---------------------------------------------------------------------------
// 金額と取引先の名前（AI が質問の書き方のまま渡す → API が決まった形に直す）
// ---------------------------------------------------------------------------

/**
 * 金額の書き方 → 円（整数）。「100万円」「1,000,000」「１００万」「1.5億」「3千円」「50万円以上」。読めなければ null。
 * AI に単位の換算をさせない（docs/90 §6「計算させない」）
 */
export function parseYen(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) && v >= 0 ? Math.round(v) : null;
  if (typeof v !== "string") return null;
  const s = v.normalize("NFKC").replace(/[,，\s円]/g, "").replace(/(以上|以下|未満|超|より.*|くらい|ほど|程度)$/u, "");
  if (!s) return null;
  const m = /^(\d+(?:\.\d+)?)(億|万|千)?$/.exec(s) ?? /^(\d+(?:\.\d+)?)(億)(\d+(?:\.\d+)?)万$/.exec(s);
  if (!m) return null;
  if (m.length === 4 && m[3]) return Math.round(Number(m[1]) * 1e8 + Number(m[3]) * 1e4);
  const unit = m[2] === "億" ? 1e8 : m[2] === "万" ? 1e4 : m[2] === "千" ? 1e3 : 1;
  const n = Math.round(Number(m[1]) * unit);
  return Number.isFinite(n) && n <= 1e12 ? n : null;
}

/** 取引先の名前から「宛て」「様」「株式会社」などを外す（探す言葉にする）。空なら null */
export function cleanPartnerName(v: string | undefined | null): string | null {
  if (!v) return null;
  const s = v
    .normalize("NFKC")
    .replace(/(宛て?|あて|向け|さん|様|殿|御中|の分|分)$/u, "")
    .replace(/^(株式会社|有限会社|合同会社|合資会社|合名会社|一般社団法人|\(株\)|\(有\)|㈱|㈲)\s*/u, "")
    .replace(/\s*(株式会社|有限会社|合同会社|\(株\)|\(有\)|㈱|㈲)$/u, "")
    .replace(/[*%＊]/g, "")
    .trim();
  return s && !/^(すべて|全て|全部|all|any)$/i.test(s) ? s.slice(0, 50) : null;
}

// ---------------------------------------------------------------------------
// 解釈の1行（API がツールの引数と結果から組み立てる。画面は AI回答の頭に1行で出す）
// ---------------------------------------------------------------------------

const ymdSlash = (s: string) => s.replace(/-/g, "/");
/** 期間の言葉（今月（2026/10/01〜2026/10/31）・全期間） */
export function periodPhrase(p: PeriodInput, today: string): string {
  const r = resolvePeriod(p, today);
  if (!r.from && !r.to) return "全期間";
  const range = `${ymdSlash(r.from ?? "")}〜${ymdSlash(r.to ?? "")}`;
  if (p.from || p.to) return range;
  if (p.month) {
    const [y, m] = p.month.split("-");
    return `${y}年${Number(m)}月（${range}）`;
  }
  const word = { this_month: "今月", last_month: "先月", this_year: "今年", last_year: "昨年", all: "全期間" }[p.period ?? "all"];
  return `${word}（${range}）`;
}

const yen = (n: number) => `${n.toLocaleString("ja-JP")}円`;
const partnerPhrase = (names: readonly string[] | null, asked: string | null): string =>
  asked == null ? "" : names && names.length > 0 ? `${names.slice(0, 3).join("・")}${names.length > 3 ? ` ほか${names.length - 3}社` : ""}宛ての` : `取引先「${asked}」（該当なし）宛ての`;

export type DocQueryDescribe = {
  kind: DocQueryKind;
  state?: DocState | undefined;
  period: PeriodInput;
  partnerAsked: string | null;
  partnerNames: readonly string[] | null;
  minAmount?: number | null;
  maxAmount?: number | null;
  sort?: "amount_desc" | "amount_asc" | "date_desc" | undefined;
  /** 集計か一覧か */
  mode: "sum" | "list";
  today: string;
};

/** 帳票の集計・一覧の解釈（「発行済の請求書・請求日が今月（2026/10/01〜2026/10/31）で集計」） */
export function describeDocQuery(d: DocQueryDescribe): string {
  const state = d.state ?? "issued";
  const kindText = DOC_QUERY_KIND_TEXT[d.kind];
  // 請求書・領収書の「発行済」に受注・失注は関わらない
  const meaning = state === "issued" && d.kind !== "estimate" ? "下書き・取消を除く" : DOC_STATE_MEANING[state];
  const head = `${partnerPhrase(d.partnerNames, d.partnerAsked)}${DOC_STATE_TEXT[state]}の${kindText}（${meaning}）`;
  const parts = [head];
  const p = periodPhrase(d.period, d.today);
  if (p !== "全期間") parts.push(`${docDateBasis(d.kind, state)}が${p}`);
  else if (!BALANCE_STATES.includes(state)) parts.push("全期間");
  if (d.minAmount != null && d.maxAmount != null) parts.push(`税込 ${yen(d.minAmount)}〜${yen(d.maxAmount)}`);
  else if (d.minAmount != null) parts.push(`税込 ${yen(d.minAmount)}以上`);
  else if (d.maxAmount != null) parts.push(`税込 ${yen(d.maxAmount)}以下`);
  if (d.sort === "amount_desc") parts.push("金額の大きい順");
  else if (d.sort === "amount_asc") parts.push("金額の小さい順");
  return `${parts.join("・")}で${d.mode === "sum" ? (BALANCE_STATES.includes(state) ? "残高を集計" : "集計") : "一覧"}`;
}

/** {project}の集計・粗利の解釈（「受注した{project}・受注日が今月（…）・粗利率 15% 未満（仕入れ未入力は対象外）」） */
export function describeGroupQuery(d: { period: PeriodInput; partnerAsked: string | null; partnerNames: readonly string[] | null; marginBelowPct?: number | undefined; metric: "total" | "margin"; sort?: string | undefined; today: string }): string {
  const parts = [`${partnerPhrase(d.partnerNames, d.partnerAsked)}受注した{project}（税抜の受注額）`];
  const p = periodPhrase(d.period, d.today);
  parts.push(p === "全期間" ? "全期間" : `受注日が${p}`);
  if (d.marginBelowPct != null) parts.push(`粗利率 ${d.marginBelowPct}% 未満`);
  if (d.metric === "margin" || d.marginBelowPct != null) parts.push("仕入れ未入力は対象外");
  if (d.sort === "amount_desc") parts.push("受注額の大きい順");
  else if (d.sort === "amount_asc") parts.push("受注額の小さい順");
  else if (d.metric === "margin") parts.push("粗利率の低い順");
  return `${parts.join("・")}で集計`;
}

/** 期間の比較の解釈 */
export function describeCompare(base: string, a: PeriodInput, b: PeriodInput, today: string): string {
  return `${base}を ${periodPhrase(a, today)} と ${periodPhrase(b, today)} で比較`;
}

/** 単価履歴の解釈 */
export const describeUnitPrice = (name: string, period: PeriodInput, today: string): string => `明細の名称に「${name}」を含む見積書の単価・${periodPhrase(period, today)}`;
/** 言葉で探すの解釈 */
export const describeSearchAll = (query: string, period: PeriodInput, today: string): string => `「${query}」で全種類を検索・${periodPhrase(period, today)}`;
/** 金額で探すの解釈 */
export const describeAmountFind = (amount: number, basis: "total" | "ex_tax", tolerance: number): string =>
  `${basis === "ex_tax" ? "税抜" : "税込"} ${yen(amount)}${tolerance > 0 ? `（±${yen(tolerance)}）` : ""}の帳票と明細を検索`;
